/**
 * API Schedule Service Implementation
 * 
 * This service provides the API implementation for schedule operations,
 * communicating with the backend scheduling endpoints to manage schedules,
 * recurrence patterns, and execution history.
 * 
 * @module ApiScheduleService
 * @implements {IScheduleService}
 * 
 * Service Description:
 * The ApiScheduleService handles all schedule-related API calls including:
 * - Schedule CRUD operations
 * - Recurrence pattern configuration
 * - Schedule activation/deactivation
 * - Execution history tracking
 * - Schedule validation
 * 
 * @requires ApiClient for HTTP requests
 * @requires IScheduleService interface
 */

import { AxiosInstance } from 'axios';
import { Schedule, RecurrencePattern, ScheduleExecution, IScheduleService } from '../../interfaces/IScheduleService';
import { ServiceResponse } from '../../interfaces/IDataSource';
import { BaseService } from '../base/BaseService';
import ApiClient from '../../../utils/apiClient';

export class ApiScheduleService extends BaseService implements IScheduleService {
  private api: AxiosInstance;
  private baseURL: string = '/api/v1/reports/schedules/';
  private executionsURL: string = '/api/v1/reports/executions/';

  constructor() {
    super('ApiScheduleService');
    this.api = ApiClient.getInstance();
    console.log('[ApiScheduleService] Initialized with base URL:', this.baseURL);
  }

  // ==================== Schedule Operations ====================
  
  async getSchedules(type?: string): Promise<Schedule[]> {
    try {
      console.log('[ApiScheduleService] Fetching schedules, type:', type);
      
      const params: any = {
        ordering: '-created_at'
      };
      
      // Filter by type if provided
      if (type) {
        params.report_type = type;
      }
      
      const response = await this.api.get(this.baseURL, { params });
      
      const schedules = response.data.results.map((schedule: any) => 
        this.transformScheduleFromAPI(schedule)
      );
      
      console.log(`[ApiScheduleService] Fetched ${schedules.length} schedules`);
      return schedules;
    } catch (error) {
      this.handleError(error, 'fetching schedules');
    }
  }

  async getSchedule(id: string): Promise<Schedule> {
    try {
      console.log(`[ApiScheduleService] Fetching schedule ${id}...`);
      const response = await this.api.get(`${this.baseURL}${id}/`);
      
      return this.transformScheduleFromAPI(response.data);
    } catch (error) {
      this.handleError(error, `fetching schedule ${id}`);
    }
  }

  async createSchedule(schedule: Partial<Schedule>): Promise<Schedule> {
    try {
      console.log('[ApiScheduleService] Creating schedule:', schedule.name);
      
      const apiData = this.transformScheduleForAPI(schedule);
      const response = await this.api.post(this.baseURL, apiData);
      
      console.log('[ApiScheduleService] Schedule created:', response.data.id);
      return this.transformScheduleFromAPI(response.data);
    } catch (error) {
      this.handleError(error, 'creating schedule');
    }
  }

  async updateSchedule(id: string, schedule: Partial<Schedule>): Promise<Schedule> {
    try {
      console.log(`[ApiScheduleService] Updating schedule ${id}...`);
      
      const apiData = this.transformScheduleForAPI(schedule);
      const response = await this.api.patch(`${this.baseURL}${id}/`, apiData);
      
      console.log(`[ApiScheduleService] Schedule ${id} updated`);
      return this.transformScheduleFromAPI(response.data);
    } catch (error) {
      this.handleError(error, `updating schedule ${id}`);
    }
  }

  async deleteSchedule(id: string): Promise<void> {
    try {
      console.log(`[ApiScheduleService] Deleting schedule ${id}...`);
      await this.api.delete(`${this.baseURL}${id}/`);
      console.log(`[ApiScheduleService] Schedule ${id} deleted successfully`);
    } catch (error) {
      this.handleError(error, `deleting schedule ${id}`);
    }
  }

  // ==================== Schedule Control ====================
  
  async activateSchedule(id: string): Promise<void> {
    try {
      console.log(`[ApiScheduleService] Activating schedule ${id}...`);
      
      // Backend uses resume endpoint for activation
      await this.api.post(`${this.baseURL}${id}/resume/`);
      
      console.log(`[ApiScheduleService] Schedule ${id} activated`);
    } catch (error) {
      this.handleError(error, `activating schedule ${id}`);
    }
  }

  async deactivateSchedule(id: string): Promise<void> {
    try {
      console.log(`[ApiScheduleService] Deactivating schedule ${id}...`);
      
      // Backend uses pause endpoint for deactivation
      await this.api.post(`${this.baseURL}${id}/pause/`);
      
      console.log(`[ApiScheduleService] Schedule ${id} deactivated`);
    } catch (error) {
      this.handleError(error, `deactivating schedule ${id}`);
    }
  }

  async runScheduleNow(id: string): Promise<ScheduleExecution> {
    try {
      console.log(`[ApiScheduleService] Running schedule ${id} now...`);
      
      // Trigger immediate execution
      const response = await this.api.post(this.executionsURL, {
        schedule_id: id,
        run_now: true
      });
      
      console.log(`[ApiScheduleService] Execution started:`, response.data.id);
      return this.transformExecutionFromAPI(response.data);
    } catch (error) {
      this.handleError(error, `running schedule ${id}`);
    }
  }

  // ==================== Execution History ====================
  
  async getExecutionHistory(scheduleId: string): Promise<ScheduleExecution[]> {
    try {
      console.log(`[ApiScheduleService] Fetching execution history for schedule ${scheduleId}...`);
      
      const response = await this.api.get(this.executionsURL, {
        params: {
          schedule_id: scheduleId,
          ordering: '-created_at'
        }
      });
      
      const executions = response.data.results.map((execution: any) => 
        this.transformExecutionFromAPI(execution)
      );
      
      console.log(`[ApiScheduleService] Fetched ${executions.length} executions`);
      return executions;
    } catch (error) {
      this.handleError(error, 'fetching execution history');
    }
  }

  async getExecution(id: string): Promise<ScheduleExecution> {
    try {
      console.log(`[ApiScheduleService] Fetching execution ${id}...`);
      const response = await this.api.get(`${this.executionsURL}${id}/`);
      
      return this.transformExecutionFromAPI(response.data);
    } catch (error) {
      this.handleError(error, `fetching execution ${id}`);
    }
  }

  // ==================== Utility Operations ====================
  
  validateRecurrence(pattern: RecurrencePattern): ServiceResponse<boolean> {
    try {
      console.log('[ApiScheduleService] Validating recurrence pattern...');
      
      const errors: string[] = [];
      
      // Validate required fields
      if (!pattern.type) {
        errors.push('Recurrence type is required');
      }
      
      if (!pattern.time || !pattern.time.match(/^\d{2}:\d{2}$/)) {
        errors.push('Time must be in HH:MM format');
      }
      
      if (!pattern.timezone) {
        errors.push('Timezone is required');
      }
      
      if (!pattern.startDate) {
        errors.push('Start date is required');
      }
      
      // Validate type-specific fields
      if (pattern.type === 'weekly' && (!pattern.daysOfWeek || pattern.daysOfWeek.length === 0)) {
        errors.push('Days of week required for weekly recurrence');
      }
      
      if (pattern.type === 'monthly' && (!pattern.dayOfMonth || pattern.dayOfMonth < 1 || pattern.dayOfMonth > 31)) {
        errors.push('Valid day of month (1-31) required for monthly recurrence');
      }
      
      if (pattern.interval < 1) {
        errors.push('Interval must be at least 1');
      }
      
      const isValid = errors.length === 0;
      
      return {
        success: isValid,
        data: isValid,
        error: isValid ? undefined : errors.join('; '),
        metadata: {
          timestamp: new Date(),
          source: 'ApiScheduleService',
          validationErrors: errors
        }
      };
    } catch (error) {
      console.error('[ApiScheduleService] Validation error:', error);
      return {
        success: false,
        data: false,
        error: 'Validation failed due to system error',
        metadata: {
          timestamp: new Date(),
          source: 'ApiScheduleService'
        }
      };
    }
  }

  getNextRunTime(pattern: RecurrencePattern): Date {
    console.log('[ApiScheduleService] Calculating next run time...');
    
    const now = new Date();
    const startDate = new Date(pattern.startDate);
    
    // Parse time
    const [hours, minutes] = pattern.time.split(':').map(Number);
    
    // If end date is passed, no next run
    if (pattern.endDate && new Date(pattern.endDate) < now) {
      throw new Error('Schedule has ended');
    }
    
    // Start from either start date or current time
    let nextRun = startDate > now ? new Date(startDate) : new Date(now);
    nextRun.setHours(hours, minutes, 0, 0);
    
    // If the time today has passed, start from tomorrow
    if (nextRun <= now) {
      nextRun.setDate(nextRun.getDate() + 1);
    }
    
    switch (pattern.type) {
      case 'once':
        // For once, return start date with specified time
        nextRun = new Date(startDate);
        nextRun.setHours(hours, minutes, 0, 0);
        if (nextRun <= now) {
          throw new Error('One-time schedule has already passed');
        }
        break;
        
      case 'daily':
        // Already handled above
        break;
        
      case 'weekly':
        if (!pattern.daysOfWeek || pattern.daysOfWeek.length === 0) {
          throw new Error('Days of week not specified for weekly schedule');
        }
        
        // Find next matching day of week
        let found = false;
        for (let i = 0; i < 7; i++) {
          const dayOfWeek = nextRun.getDay();
          if (pattern.daysOfWeek.includes(dayOfWeek)) {
            found = true;
            break;
          }
          nextRun.setDate(nextRun.getDate() + 1);
        }
        
        if (!found) {
          throw new Error('No valid days of week specified');
        }
        break;
        
      case 'monthly':
        if (!pattern.dayOfMonth) {
          throw new Error('Day of month not specified for monthly schedule');
        }
        
        // Set to the specified day of month
        nextRun.setDate(pattern.dayOfMonth);
        
        // If we've passed this month's date, go to next month
        if (nextRun <= now) {
          nextRun.setMonth(nextRun.getMonth() + 1);
        }
        break;
        
      case 'yearly':
        // Advance by years until we find a future date
        while (nextRun <= now) {
          nextRun.setFullYear(nextRun.getFullYear() + 1);
        }
        break;
        
      default:
        throw new Error(`Unknown recurrence type: ${pattern.type}`);
    }
    
    // Apply interval if greater than 1
    if (pattern.interval > 1) {
      // This is simplified - real implementation would need more complex logic
      console.warn('[ApiScheduleService] Interval > 1 not fully implemented');
    }
    
    console.log('[ApiScheduleService] Next run time:', nextRun);
    return nextRun;
  }

  async getUpcomingSchedules(days: number): Promise<Schedule[]> {
    try {
      console.log(`[ApiScheduleService] Fetching schedules for next ${days} days...`);
      
      // Get all active schedules
      const response = await this.api.get(this.baseURL, {
        params: {
          is_active: true,
          ordering: 'next_run'
        }
      });
      
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() + days);
      
      // Filter schedules that will run within the specified days
      const schedules = response.data.results
        .map((schedule: any) => this.transformScheduleFromAPI(schedule))
        .filter((schedule: Schedule) => {
          if (!schedule.nextRun) return false;
          return new Date(schedule.nextRun) <= cutoffDate;
        });
      
      console.log(`[ApiScheduleService] Found ${schedules.length} upcoming schedules`);
      return schedules;
    } catch (error) {
      this.handleError(error, 'fetching upcoming schedules');
    }
  }

  // ==================== Private Helper Methods ====================
  
  private transformScheduleFromAPI(data: any): Schedule {
    // Calculate next run time if schedule is active
    let nextRun: Date | undefined;
    if (data.is_active && data.schedule_config) {
      try {
        const pattern = this.transformRecurrenceFromAPI(data.schedule_config);
        nextRun = this.getNextRunTime(pattern);
      } catch (error) {
        console.warn('[ApiScheduleService] Could not calculate next run time:', error);
      }
    }
    
    return {
      id: data.id,
      name: data.name,
      description: data.description || '',
      type: this.mapScheduleType(data.report_type),
      entityId: data.id, // For reports, entity ID is the schedule ID itself
      recurrence: this.transformRecurrenceFromAPI(data.schedule_config || {}),
      isActive: data.is_active || false,
      nextRun,
      lastRun: data.last_execution?.created_at ? new Date(data.last_execution.created_at) : undefined,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    };
  }

  private transformScheduleForAPI(schedule: Partial<Schedule>): any {
    const apiData: any = {};
    
    if (schedule.name !== undefined) apiData.name = schedule.name;
    if (schedule.description !== undefined) apiData.description = schedule.description;
    if (schedule.type !== undefined) apiData.report_type = this.mapScheduleTypeToAPI(schedule.type);
    if (schedule.isActive !== undefined) apiData.is_active = schedule.isActive;
    
    if (schedule.recurrence) {
      apiData.schedule_config = this.transformRecurrenceForAPI(schedule.recurrence);
    }
    
    // Add default parameters for report schedules
    if (!apiData.parameters) {
      apiData.parameters = {};
    }
    
    return apiData;
  }

  private transformRecurrenceFromAPI(config: any): RecurrencePattern {
    return {
      type: config.frequency || 'once',
      interval: config.interval || 1,
      daysOfWeek: config.days_of_week,
      dayOfMonth: config.day_of_month,
      time: config.time || '00:00',
      timezone: config.timezone || 'UTC',
      startDate: config.start_date ? new Date(config.start_date) : new Date(),
      endDate: config.end_date ? new Date(config.end_date) : undefined
    };
  }

  private transformRecurrenceForAPI(pattern: RecurrencePattern): any {
    return {
      frequency: pattern.type,
      interval: pattern.interval,
      days_of_week: pattern.daysOfWeek,
      day_of_month: pattern.dayOfMonth,
      time: pattern.time,
      timezone: pattern.timezone,
      start_date: pattern.startDate.toISOString(),
      end_date: pattern.endDate?.toISOString()
    };
  }

  private transformExecutionFromAPI(data: any): ScheduleExecution {
    return {
      id: data.id,
      scheduleId: data.schedule_id || data.schedule,
      status: data.status,
      startTime: new Date(data.created_at),
      endTime: data.completed_at ? new Date(data.completed_at) : undefined,
      result: data.artifact || data.result,
      error: data.error_message
    };
  }

  private mapScheduleType(reportType: string): 'report' | 'assessment' | 'task' | 'reminder' {
    // Map backend report types to frontend schedule types
    // For now, all backend schedules are report-based
    return 'report';
  }

  private mapScheduleTypeToAPI(type: string): string {
    // Map frontend schedule types to backend report types
    switch (type) {
      case 'report':
        return 'custom';
      case 'assessment':
        return 'assessment';
      case 'task':
        return 'task';
      case 'reminder':
        return 'reminder';
      default:
        return 'custom';
    }
  }

  private handleError(error: any, operation: string): never {
    console.error(`[ApiScheduleService] Error during ${operation}:`, error);
    
    // Enhanced error handling
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.detail || error.response.data?.message || error.message;
      
      switch (status) {
        case 400:
          throw new Error(`Invalid request: ${message}`);
        case 401:
          throw new Error('Authentication required');
        case 403:
          throw new Error('You do not have permission to perform this action');
        case 404:
          throw new Error('Schedule not found');
        case 500:
          throw new Error('Server error. Please try again later');
        default:
          throw new Error(message || 'An unexpected error occurred');
      }
    }
    
    // Network or other errors
    throw new Error(error.message || 'Network error. Please check your connection');
  }
}